import { makeStyles } from '@material-ui/core/styles';
import { redesignTheme } from '../../../theme';

const { colors, spacing, breakpoints, fonts, fontSize, radius, sizes } = redesignTheme;

const contentMaxWidth = sizes.ebookContentMaxWidth;

const cover = {
  header: {
    width: '92%',
    top: '7%',
    left: '53%',
  },
  typography: {
    number: { size: '11%', min: '5%', max: '16%' },
    title: { size: '6%', min: '2%', max: '6%' },
    titleSmall: { size: '4.8%', min: '4%', max: '6.5%' },
    subtitle: { size: '2.4%', min: '1%', max: '4%' },
    decoration: { size: '3.7%', min: '2.5%', max: '4.5%' },
    sideLabel: { size: '2.8%', min: '2%', max: '3.5%' },
    footer: { size: '1.4%', min: '1%', max: '3.5%' },
    stepTitle: { size: '3.4%', min: '3.2%', max: '5%' },
    stepText: { size: '2%', min: '1.6%', max: '2.6%' },
  },
  spacing: {
    wrapGap: '0.8%',
    numberMargin: '0.6%',
    subtitleLetterSpacing: '0.35%',
    subtitleMargin: '0.6%',
    decorationMargin: '0.5%',
    decorationOffset: '-1.2%',
    stepTitleMargin: '0.35%',
    stepTextPadding: '0.4%',
    stepLineHeight: '1.1',
    stepLetterSpacing: '0.12%',
    stepDividerWidth: '3.5%',
    stepDividerHeight: '0.35%',
    stepDividerOffset: '1.4%',
    stepContentPaddingY: '0.9%',
  },
  position: {
    sideLabel: { top: '13%', left: '0.5%' },
    footer: { bottom: '3%', left: '52%' },
    stepBlock: { left: '76%', translateY: '85%' },
    stepIntro: { top: '52%' },
    stepOne: { top: '40%' },
    stepTwo: { top: '31%' },
    stepThree: { top: '37%' },
    stepFour: { top: '43%' },
    stepFive: { top: '34%' },
  },
} as const;

const toCqw = (value: string) => `${parseFloat(value)}cqw`;

const coverSize = ({ size, min, max }: { size: string; min: string; max: string }) =>
  `clamp(${toCqw(min)}, ${toCqw(size)}, ${toCqw(max)})`;

const coverPageCard =
  '& [id^="page-"] > .MuiGrid-container > .MuiGrid-item > .MuiCard-root:has(> img)';
const innerPageCard =
  '& [id^="page-"] > .MuiGrid-container > .MuiGrid-item > .MuiCard-root:has(> .MuiCardContent-root)';
// Direct-child img only: TitleChapitre is Grid > item > img. A descendant
// selector also matched the outer page Grid and sized those pages to content.
const chapterBanner =
  '& .MuiGrid-container:has(> .MuiGrid-item > img[alt="Chapitre title"])';

export const useEbookReaderLayoutStyles = makeStyles(() => ({
  page: {
    minHeight: '100vh',
    backgroundColor: colors.background,
    boxSizing: 'border-box',
    scrollBehavior: 'smooth',
    overflowX: 'hidden',
    padding: `${spacing.md}px clamp(${spacing.sm}px, 4vw, 120px) ${spacing.xl}px`,
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      padding: `${spacing.md}px 0 ${spacing.xl}px`,
    },
  },
  loadingCenter: {
    minHeight: 'calc(100vh - 80px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: fonts.opensans,
    fontSize: fontSize.base,
    color: colors.primaryDark,
  },
  ebookContent: {
    width: '100%',
    maxWidth: contentMaxWidth,
    margin: '0 auto',
    boxSizing: 'border-box',
    scrollMarginTop: 96,
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      maxWidth: '100%',
      width: '100%',
    },
    '& [id^="page-"]': {
      scrollMarginTop: 96,
      marginBottom: spacing.md,
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
      [`@media (max-width: ${breakpoints.mobile}px)`]: {
        marginTop: '0 !important',
        marginBottom: spacing.sm,
      },
    },
    '& .MuiGrid-container': {
      margin: '0 !important',
      width: '100%',
      minWidth: 0,
    },
    '& [id^="page-"] > .MuiGrid-container': {
      width: '100% !important',
      maxWidth: '100%',
      minWidth: 0,
    },
    '& [id^="page-"] > .MuiGrid-container > .MuiGrid-item': {
      flexBasis: '100%',
      maxWidth: '100%',
      minWidth: 0,
      position: 'relative',
    },
    '& [id^="page-"] > .MuiGrid-container > .MuiGrid-grid-xs-12': {
      flexBasis: '100%',
      maxWidth: '100%',
      minWidth: 0,
    },
    '& [id^="page-"] > .MuiGrid-container > .MuiGrid-grid-md-8': {
      flexBasis: '100%',
      maxWidth: '100%',
      minWidth: 0,
    },
    [chapterBanner]: {
      display: 'inline-flex !important',
      flexWrap: 'nowrap !important',
      alignItems: 'center !important',
      width: 'auto !important',
      maxWidth: '100% !important',
      height: 'auto !important',
      minHeight: '40px',
      '& .MuiGrid-item': {
        flexBasis: 'auto !important',
        maxWidth: 'none !important',
        width: 'auto !important',
        flexGrow: '0 !important',
      },
      '& img': {
        display: 'block',
        height: 'auto',
        maxHeight: '100%',
      },
      '& .MuiGrid-item > div': {
        whiteSpace: 'nowrap',
      },
    },
    [innerPageCard]: {
        width: '100%',
        maxWidth: '100%',
        minHeight: 'unset !important',
        height: 'fit-content !important',
        maxHeight: 'none !important',
        borderRadius: '0 !important',
        boxShadow: '0 18px 40px rgba(14, 18, 71, 0.08) !important',
        boxSizing: 'border-box',
        overflow: 'hidden',
        display: 'block !important',
        padding: `${spacing.lg}px !important`,
        [`@media (max-width: ${breakpoints.tablet}px)`]: {
          padding: `${spacing.md}px !important`,
        },
        [`@media (max-width: ${breakpoints.mobile}px)`]: {
          padding: `${spacing.sm}px !important`,
          borderRadius: '0 !important',
          boxShadow: 'none !important',
        },
      },
    '& .MuiCardContent-root .MuiCard-root, & .MuiCard-root .MuiCard-root': {
      padding: '0 !important',
      margin: '24px 0px!important',
      minHeight: 'unset !important',
      height: 'auto !important',
      borderRadius: '10px !important',
      boxShadow: 'none !important',
      overflow: 'hidden',
      boxSizing: 'border-box',
      '& .MuiGrid-spacing-xs-2': {
        margin: '0 !important',
        width: '100%',
      },
      '& .MuiGrid-spacing-xs-2 > .MuiGrid-item': {
        padding: '0 !important',
      },
      '& .MuiGrid-justify-content-xs-flex-end': {
        alignItems: 'stretch !important',
      },
      '& .MuiGrid-justify-content-xs-flex-end > .MuiGrid-item:not(:has(.MuiTextField-root)):not(:has(textarea))':
        {
          display: 'flex !important',
          flexDirection: 'column !important',
          alignItems: 'center !important',
          justifyContent: 'center !important',
          padding: `${spacing.sm}px`,
          boxSizing: 'border-box',
        },
      '& .MuiGrid-justify-content-xs-flex-end > .MuiGrid-item:not(:has(.MuiTextField-root)) img': {
        maxWidth: '100% !important',
        height: 'auto !important',
      },
      '& .MuiGrid-justify-content-xs-flex-end > .MuiGrid-item:has(.MuiTextField-root), & .MuiGrid-justify-content-xs-flex-end > .MuiGrid-item:has(textarea)':
        {
          padding: '0 !important',
          display: 'block !important',
        },
      '& .MuiTextField-root, & textarea': {
        width: '100% !important',
        margin: '0 !important',
      },
      // Header plus one body only. A later row must match the rows above it.
      // Select columns draw their own borders, so that inset would stop them short of the card edge.
      '& > .MuiGrid-spacing-xs-2 > .MuiGrid-item > div:not(.MuiCard-root) > div:nth-child(2):last-child:not(:has(.MuiSelect-root))':
        {
          padding: `${spacing.sm}px !important`,
          boxSizing: 'border-box',
        },
      // The card outline is the last row's bottom edge. Match the black row dividers
      // and keep that corner square so the number badge is not clipped.
      // borderTable > cardBgGrey is the synthesis table (page 40); its last row is the card edge.
      '&:has([class*="floatIndex"]), &:has(.MuiSelect-root), &:has([class*="borderTable"] > [class*="cardBgGrey"])':
        {
          borderBottomColor: '#000 !important',
          borderBottomLeftRadius: '0 !important',
          borderBottomRightRadius: '0 !important',
        },
      '& .MuiTypography-root': {
        margin: '0 !important',
      },
      '& .MuiTypography-h2': {
        margin: '0 !important',
        lineHeight: '1.2 !important',
      },
    },
    [coverPageCard]: {
      width: '100% !important',
      maxWidth: '100% !important',
      marginLeft: '0 !important',
      minHeight: 'auto !important',
      overflow: 'hidden',
      position: 'relative',
      padding: '0 !important',
      containerType: 'inline-size',
      [`@media (max-width: ${breakpoints.mobile}px)`]: {
        borderRadius: '0 !important',
        boxShadow: 'none !important',
      },
      '& .position-title-cover, & .position-title-cover-2': {
        position: 'absolute',
        left: `${cover.header.left} !important`,
        top: `${cover.header.top} !important`,
        transform: 'translateX(-50%) !important',
        width: cover.header.width,
        maxWidth: '100%',
        padding: '0 !important',
        margin: '0 !important',
        display: 'flex',
        justifyContent: 'center',
      },
      '& .position-title-cover-2': {
        position: 'relative',
        top: 'auto !important',
        left: 'auto !important',
        transform: 'none !important',
        width: '100%',
        margin: '16px 0 !important',
      },
      '& .header__content-wrap, & .header__content-wrap-2': {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '100%',
        flexWrap: 'nowrap',
        gap: toCqw(cover.spacing.wrapGap),
      },
      '& .header__content-wrap-2': {
        alignItems: 'center',
      },
      '& .header__content-title': {
        margin: '0 !important',
        width: '100%',
      },
      '& .header__content-text': {
        paddingTop: '0 !important',
        width: '100%',
      },
      '& .header__content-value': {
        display: 'inline-flex',
        alignItems: 'flex-start',
        flexShrink: 0,
      },
      '& .header__content-value .number': {
        fontSize: `${coverSize(cover.typography.number)} !important`,
        lineHeight: '1 !important',
        marginRight: `${toCqw(cover.spacing.numberMargin)} !important`,
      },
      '& .header__content-desc': {
        display: 'inline-flex !important',
        flexDirection: 'column',
        whiteSpace: 'nowrap !important',
        minWidth: 0,
      },
      '& .header__content-desc .title, & .header__content-desc .title-2': {
        fontSize: `${coverSize(cover.typography.title)} !important`,
        lineHeight: '1 !important',
        margin: '0 !important',
      },
      '& .header__content-desc .title-2 .span-votre': {
        fontSize: `${coverSize(cover.typography.titleSmall)} !important`,
      },
      '& .header__content-desc .subtitle': {
        fontSize: `${coverSize(cover.typography.subtitle)} !important`,
        letterSpacing: `${toCqw(cover.spacing.subtitleLetterSpacing)} !important`,
        marginLeft: `${toCqw(cover.spacing.subtitleMargin)} !important`,
      },
      '& .decoration': {
        width: `${coverSize(cover.typography.decoration)} !important`,
        flexShrink: 0,
        marginTop: '0 !important',
      },
      '& .header__content .decoration': {
        marginTop: `${toCqw(cover.spacing.decorationOffset)} !important`,
      },
      '& .decoration--blue': {
        marginRight: `${toCqw(cover.spacing.decorationMargin)} !important`,
      },
      '& .decoration--yellow': {
        marginLeft: `${toCqw(cover.spacing.decorationMargin)} !important`,
      },
      '& .title-aside-side, & .title-aside': {
        top: `${cover.position.sideLabel.top} !important`,
        left: `${cover.position.sideLabel.left} !important`,
        fontSize: `${coverSize(cover.typography.sideLabel)} !important`,
      },
      '& .footer-title': {
        fontSize: `${coverSize(cover.typography.footer)} !important`,
        bottom: `${cover.position.footer.bottom} !important`,
        left: `${cover.position.footer.left} !important`,
        transform: 'translate(-50%, 50%) !important',
      },
      '& .right-column.position-absolute-cover': {
        left: `${cover.position.stepBlock.left} !important`,
        transform: `translate(-50%, ${cover.position.stepBlock.translateY}) !important`,
      },
      '& .main-title-intro': {
        top: `${cover.position.stepIntro.top} !important`,
      },
      '& .main-title-step-one': {
        top: `${cover.position.stepOne.top} !important`,
      },
      '& .main-title-step-two': {
        top: `${cover.position.stepTwo.top} !important`,
      },
      '& .main-title-step-three': {
        top: `${cover.position.stepThree.top} !important`,
      },
      '& .main-title-step-four': {
        top: `${cover.position.stepFour.top} !important`,
      },
      '& .main-title-step-five': {
        top: `${cover.position.stepFive.top} !important`,
      },
      '& .main-title-h2': {
        fontSize: `${coverSize(cover.typography.stepTitle)} !important`,
        paddingTop: '0 !important',
        margin: '0 !important',
        marginBottom: `${toCqw(cover.spacing.stepTitleMargin)} !important`,
        lineHeight: `${cover.spacing.stepLineHeight} !important`,
      },
      '& .right-column__content': {
        paddingLeft: '0 !important',
        paddingTop: `${toCqw(cover.spacing.stepContentPaddingY)} !important`,
        paddingBottom: `${toCqw(cover.spacing.stepContentPaddingY)} !important`,
        marginBottom: '0 !important',
        '&::before, &::after': {
          width: `${toCqw(cover.spacing.stepDividerWidth)} !important`,
          height: `${toCqw(cover.spacing.stepDividerHeight)} !important`,
        },
        '&::before': {
          top: `-${toCqw(cover.spacing.stepDividerOffset)} !important`,
        },
        '&::after': {
          bottom: `-${toCqw(cover.spacing.stepDividerOffset)} !important`,
        },
      },
      '& .right-column__content p, & .right-column__content .text-font-line-height': {
        fontSize: `${coverSize(cover.typography.stepText)} !important`,
        lineHeight: `${cover.spacing.stepLineHeight} !important`,
        letterSpacing: `${toCqw(cover.spacing.stepLetterSpacing)} !important`,
        margin: '0 !important',
        paddingBottom: `${toCqw(cover.spacing.stepTextPadding)} !important`,
      },
      '& .right-column__content p + p': {
        paddingBottom: '0 !important',
        marginTop: `${toCqw(cover.spacing.stepTextPadding)} !important`,
      },
    },
    [`${coverPageCard} > img`]: {
      width: '100%',
      height: 'auto',
      display: 'block',
    },
    '& .MuiCardContent-root': {
      boxSizing: 'border-box',
      overflowX: 'auto',
      overflowWrap: 'break-word',
      minHeight: 'auto !important',
      height: 'auto !important',
      padding: `${spacing.sm}px !important`,
      paddingBottom: `${spacing.xl}px !important`,
      [`@media (max-width: ${breakpoints.mobile}px)`]: {
        padding: `${spacing.xs}px !important`,
        paddingBottom: `${spacing.lg}px !important`,
      },
      '&:last-child': {
        paddingBottom: spacing.xl,
      },
    },
    '& .MuiCardContent-root *': {
      minHeight: 'auto !important',
    },
    '& .MuiCardContent-root > div': {
      maxWidth: '100%',
    },
    '& img, & video, & iframe, & svg': {
      maxWidth: '100%',
      height: 'auto',
    },
    '& table': {
      maxWidth: '100%',
      width: '100% !important',
      tableLayout: 'fixed',
      wordBreak: 'break-word',
      display: 'block',
      overflowX: 'auto',
    },
    '& input, & select': {
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
    '& .MuiGrid-item:has(.MuiTextField-root), & .MuiGrid-item:has(textarea)': {
      padding: '0 !important',
    },
    '& .MuiFormControl-root:has(textarea), & .MuiTextField-root': {
      width: '100%',
      margin: '0 !important',
    },
    '& .MuiCard-root:has(> .MuiCardContent-root) .header__content-desc': {
      whiteSpace: 'normal',
    },
    '& [id^="page-"] > .MuiGrid-container > .MuiGrid-item > h2': {
      position: 'absolute',
      bottom: spacing.lg,
      right: spacing.lg,
      float: 'none',
      margin: 0,
      padding: 0,
      zIndex: 2,
      fontFamily: fonts.opensans,
      fontSize: '1.5em',
      color: '#2B56A5',
      lineHeight: 1,
      pointerEvents: 'none',
      [`@media (max-width: ${breakpoints.tablet}px)`]: {
        bottom: spacing.md,
        right: spacing.md,
      },
      [`@media (max-width: ${breakpoints.mobile}px)`]: {
        bottom: spacing.sm,
        right: spacing.sm,
        fontSize: '1.25em',
      },
    },
  },
  footerWrap: {
    width: '100%',
    maxWidth: contentMaxWidth,
    margin: `${spacing.lg}px auto 96px`,
    boxSizing: 'border-box',
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      maxWidth: '100%',
      paddingLeft: spacing.sm,
      paddingRight: spacing.sm,
    },
    '& button': {
      backgroundColor: `${colors.primaryDark} !important`,
      borderRadius: `${radius.sm}px !important`,
      boxShadow: 'none !important',
      textTransform: 'none',
    },
  },
}));
